import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Building2, Package, Clock, CheckCircle2, Ruler, Square, ShoppingBag, ArrowUpRight } from "lucide-react";
import { fmtNum, formatDate, STATUS_COLORS } from "@/lib/constants";

function Stat({ icon: Icon, label, value, suffix }) {
  return (
    <div className="bg-white border border-[#eae5dc] p-6" data-testid={`admin-stat-${label.replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="label-tag">{label}</span>
        <Icon size={18} className="text-[#c8a97e]" strokeWidth={1.5} />
      </div>
      <div className="font-serif text-4xl mt-4">
        {fmtNum(value)} <span className="text-base text-[#6b655c]">{suffix}</span>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/admin/dashboard").then(({ data }) => setD(data)).catch(() => {});
  }, []);
  if (!d)
    return <div className="text-[#6b655c]">Yükleniyor…</div>;

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <span className="label-tag">Genel Bakış</span>
        <h1 className="font-serif text-4xl mt-2">Dashboard</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        <Stat icon={Building2} label="Toplam Firma" value={d.total_companies} />
        <Stat icon={Package} label="Aktif Ürün" value={d.active_products} />
        <Stat icon={Clock} label="Bekleyen Sipariş" value={d.pending_orders} />
        <Stat icon={CheckCircle2} label="Onaylanan" value={d.approved_orders} />
        <Stat icon={ShoppingBag} label="Sipariş Satırı" value={d.total_lines} />
        <Stat icon={Ruler} label="Toplam Uzunluk" value={(d.total_meters || 0) * 100} suffix="cm" />
        <Stat icon={Square} label="Toplam m²" value={d.total_sqm} suffix="m²" />
        <Stat icon={CheckCircle2} label="Tamamlanan" value={d.completed_orders} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-[#eae5dc] p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="font-serif text-xl">Son Siparişler</h3>
            <Link to="/admin/siparisler" className="text-xs uppercase tracking-[0.2em] text-[#c8a97e] hover:underline flex items-center gap-1">
              Tümü <ArrowUpRight size={14} />
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wider text-[#6b655c] border-b border-[#eae5dc]">
                  <th className="text-left py-3">Sipariş No</th>
                  <th className="text-left py-3">Firma</th>
                  <th className="text-left py-3">Tarih</th>
                  <th className="text-left py-3">Durum</th>
                  <th className="text-right py-3">m²</th>
                </tr>
              </thead>
              <tbody>
                {d.recent_orders.length === 0 && (
                  <tr><td colSpan={5} className="py-8 text-center text-[#6b655c]">Sipariş yok</td></tr>
                )}
                {d.recent_orders.map((o) => (
                  <tr key={o.id} className="border-b border-[#f0ebe2]">
                    <td className="py-3">
                      <Link to={`/admin/siparisler/${o.id}`} className="hover:text-[#c8a97e]">{o.order_no}</Link>
                    </td>
                    <td className="py-3">{o.company_name}</td>
                    <td className="py-3 text-[#6b655c]">{formatDate(o.created_at)}</td>
                    <td className="py-3">
                      <span className={`text-xs px-2 py-1 ${STATUS_COLORS[o.status] || "bg-stone-100"}`}>{o.status}</span>
                    </td>
                    <td className="py-3 text-right font-medium">{fmtNum(o.total_sqm)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white border border-[#eae5dc] p-6">
          <h3 className="font-serif text-xl mb-6">En Çok Sipariş Verilen</h3>
          <div className="space-y-4">
            {d.top_products.length === 0 && <p className="text-[#6b655c] text-sm">Veri yok</p>}
            {d.top_products.map((p, i) => (
              <div key={p._id} className="flex items-start justify-between gap-3 pb-4 border-b border-[#f0ebe2] last:border-0">
                <div className="flex gap-3">
                  <span className="text-[#c8a97e] font-serif text-xl w-6">{i + 1}</span>
                  <div>
                    <div className="font-medium">{p.name}</div>
                    <div className="text-xs text-[#6b655c]">{fmtNum(p.qty)} adet</div>
                  </div>
                </div>
                <div className="text-sm font-medium">{fmtNum(p.sqm)} m²</div>
              </div>
            ))}
          </div>

          <h3 className="font-serif text-xl mt-8 mb-6">En Aktif Firmalar</h3>
          <div className="space-y-4">
            {d.top_dealers.length === 0 && <p className="text-[#6b655c] text-sm">Veri yok</p>}
            {d.top_dealers.map((c, i) => (
              <div key={c._id} className="flex items-start justify-between gap-3 pb-4 border-b border-[#f0ebe2] last:border-0">
                <div className="flex gap-3">
                  <span className="text-[#c8a97e] font-serif text-xl w-6">{i + 1}</span>
                  <div>
                    <div className="font-medium">{c.name}</div>
                    <div className="text-xs text-[#6b655c]">{c.orders} sipariş</div>
                  </div>
                </div>
                <div className="text-sm font-medium">{fmtNum(c.sqm)} m²</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
